import type {
  IdentityLookupRow,
  IdentityNeighborhoodImageRef,
} from '../api/identityVerificationDashboardApi';
import type { IdentityAttemptPresentation } from './identityAttemptPresentation';

export type IdentityMatchInsightRole = 'primary_hit' | 'near_match';

/** Dedup check chip on a neighborhood row (hover shows compared values). */
export type IdentityMatchSignal = {
  id: string;
  label: string;
  /** Hover text — document #, name/DOB, biometric distance, etc. */
  tooltip: string | null;
};

export type IdentityMatchInsightRow = {
  id: string;
  role: IdentityMatchInsightRole;
  /** 1 = primary hit, then nearest neighbors. */
  rank: number;
  name: string | null;
  ccid: string;
  email: string | null;
  phone: string | null;
  distance: number | null;
  accountStatus: string | null;
  /** Which dedup checks fired (face / DOB / name / document / etc.). */
  matchSignals: IdentityMatchSignal[];
  selfie: IdentityNeighborhoodImageRef | null;
  document: IdentityNeighborhoodImageRef | null;
};

export type IdentityMatchInsights = {
  /** Source attempt used for the neighborhood. */
  sourceVerificationId: string | null;
  /** Dedup message / what matched line when present. */
  matchSummary: string | null;
  rows: IdentityMatchInsightRow[];
};

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function str(v: unknown): string | null {
  if (typeof v === 'string') {
    const t = v.trim();
    return t.length > 0 ? t : null;
  }
  return null;
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function displayName(parts: {
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
}): string | null {
  const joined = [parts.first_name, parts.last_name].filter(Boolean).join(' ').trim();
  if (joined) return joined;
  return str(parts.name);
}

function parseImageRef(v: unknown): IdentityNeighborhoodImageRef | null {
  const r = asRecord(v);
  if (!r) return null;
  const filePath = str(r.file_path);
  if (!filePath) return null;
  return {
    uploaded_image_id: str(r.uploaded_image_id),
    file_path: filePath,
    image_type: str(r.image_type) ?? 'image',
    iv_verification_id: str(r.iv_verification_id),
    storage_kind: str(r.storage_kind),
  };
}

function hitSignalsFromDedup(
  dedup: NonNullable<IdentityLookupRow['latestDedup']>,
  source: IdentityLookupRow | null,
): IdentityMatchSignal[] {
  const hit = dedup.matched_hit_customer;
  const candidate = asRecord(dedup.candidate_match);
  const ocr = asRecord(source?.ocr_data) ?? {};

  const incomingDoc =
    str(ocr.document_number) ||
    str(ocr.documentNumber) ||
    str(ocr.document_number_normalized);
  const incomingName = displayName({
    first_name: str(ocr.first_name) || str(ocr.firstName),
    last_name: str(ocr.last_name) || str(ocr.lastName),
    name: str(ocr.full_name) || str(ocr.fullName),
  });
  const incomingDob =
    str(ocr.date_of_birth) || str(ocr.dateOfBirth) || str(ocr.dob);

  const hitName = hit
    ? displayName(hit)
    : displayName({
        first_name: str(candidate?.first_name),
        last_name: str(candidate?.last_name),
        name: str(candidate?.name),
      });
  const hitDob = str(hit?.date_of_birth);

  const nameLine = hitName || incomingName;
  const dobLine = hitDob || incomingDob;
  const piiTooltipParts = [
    nameLine ? `Name: ${nameLine}` : null,
    dobLine ? `Date of birth: ${dobLine}` : null,
  ].filter(Boolean);
  const piiTooltip = piiTooltipParts.length > 0 ? piiTooltipParts.join('\n') : null;

  const candidateDistance = num(candidate?.distance);

  const fromEval = (dedup.tier_evaluation ?? [])
    .filter((t) => t.hit)
    .map((t): IdentityMatchSignal | null => {
      const label = str(t.label) || str(t.tier);
      if (!label) return null;
      const tierKey = str(t.tier)?.toLowerCase() ?? '';
      const details = asRecord(t.details) ?? {};
      const bioDist =
        num(details.biometric_distance) ??
        num(details.distance) ??
        candidateDistance;

      let tooltip: string | null = null;
      if (tierKey === 'tier0' || /document\s*number/i.test(label)) {
        tooltip = incomingDoc
          ? `Document number: ${incomingDoc}`
          : 'Document numbers matched (value not on this session).';
      } else if (tierKey === 'tier1' || /stable\s*pii/i.test(label)) {
        tooltip = piiTooltip ?? 'Name and date of birth matched.';
      } else if (tierKey === 'tier2' || /direct\s*biometric/i.test(label)) {
        tooltip =
          bioDist != null
            ? `Biometric distance: ${bioDist.toFixed(4)}`
            : 'Biometric distance not available.';
      } else if (tierKey === 'tier3' || /pii\s*&\s*biometric/i.test(label)) {
        const parts = [
          bioDist != null ? `Biometric distance: ${bioDist.toFixed(4)}` : null,
          ...(piiTooltipParts as string[]),
        ].filter(Boolean);
        tooltip = parts.length > 0 ? parts.join('\n') : null;
      }

      return {
        id: `${tierKey || label}:${label}`,
        label,
        tooltip,
      };
    })
    .filter((x): x is IdentityMatchSignal => x != null);

  if (fromEval.length > 0) {
    const seen = new Set<string>();
    return fromEval.filter((s) => {
      if (seen.has(s.label)) return false;
      seen.add(s.label);
      return true;
    });
  }

  const tiers = dedup.matched_tiers ?? [];
  if (tiers.length === 0) return [];
  const labelByTier = new Map(
    (dedup.tier_evaluation ?? []).map((t) => [t.tier, str(t.label) || t.tier]),
  );
  return [...new Set(tiers)].map((tier) => {
    const label = labelByTier.get(tier) || tier;
    const tierKey = tier.toLowerCase();
    let tooltip: string | null = null;
    if (tierKey === 'tier0' || /document\s*number/i.test(label)) {
      tooltip = incomingDoc
        ? `Document number: ${incomingDoc}`
        : 'Document numbers matched (value not on this session).';
    } else if (tierKey === 'tier1' || /stable\s*pii/i.test(label)) {
      tooltip = piiTooltip ?? 'Name and date of birth matched.';
    } else if (tierKey === 'tier2' || /direct\s*biometric/i.test(label)) {
      tooltip =
        candidateDistance != null
          ? `Biometric distance: ${candidateDistance.toFixed(4)}`
          : 'Biometric distance not available.';
    } else if (tierKey === 'tier3' || /pii\s*&\s*biometric/i.test(label)) {
      const parts = [
        candidateDistance != null
          ? `Biometric distance: ${candidateDistance.toFixed(4)}`
          : null,
        ...(piiTooltipParts as string[]),
      ].filter(Boolean);
      tooltip = parts.length > 0 ? parts.join('\n') : null;
    }
    return { id: `${tier}:${label}`, label, tooltip };
  });
}

function resolveSourceCcid(source: IdentityLookupRow | null): string | null {
  return str(source?.ccid) || str(source?.customer?.ccid);
}

/** Neighborhood rows require a confirmed duplicate — cleared dedup may still list vector neighbors. */
function hasConfirmedDuplicateHit(
  dedup: NonNullable<IdentityLookupRow['latestDedup']>,
): boolean {
  if (dedup.isDuplicate === true) return true;
  if (dedup.isDuplicate === false) return false;
  // Legacy rows may omit isDuplicate but still carry matched_tiers.
  return (dedup.matched_tiers?.length ?? 0) > 0;
}

function isSameCcid(a: string | null, b: string | null): boolean {
  return a != null && b != null && a === b;
}

function pickSourceRow(
  attempts: IdentityAttemptPresentation[],
  fallbackRows: IdentityLookupRow[],
): IdentityLookupRow | null {
  const fromAttempts =
    attempts.find((a) => a.role === 'existing_found')?.row ??
    attempts.find((a) => a.row.latestDedup?.isDuplicate === true)?.row ??
    attempts.find(
      (a) =>
        a.row.latestDedup?.isDuplicate !== false &&
        (a.row.latestDedup?.matched_tiers?.length ?? 0) > 0,
    )?.row ??
    attempts[0]?.row ??
    null;
  if (fromAttempts) return fromAttempts;

  return (
    fallbackRows.find((r) => r.latestDedup?.isDuplicate === true) ??
    fallbackRows.find(
      (r) =>
        r.latestDedup?.isDuplicate !== false &&
        (r.latestDedup?.matched_tiers?.length ?? 0) > 0,
    ) ??
    fallbackRows[0] ??
    null
  );
}

/**
 * Build operator-facing neighborhood rows: primary duplicate hit + closest candidates
 * from face / DOB / name / address / document search.
 */
export function buildIdentityMatchInsights(
  attemptsOrRows: IdentityAttemptPresentation[] | IdentityLookupRow[],
): IdentityMatchInsights {
  const attempts: IdentityAttemptPresentation[] =
    attemptsOrRows.length > 0 && 'role' in attemptsOrRows[0]!
      ? (attemptsOrRows as IdentityAttemptPresentation[])
      : [];
  const rowsOnly: IdentityLookupRow[] =
    attempts.length > 0
      ? attempts.map((a) => a.row)
      : (attemptsOrRows as IdentityLookupRow[]);

  const source = pickSourceRow(attempts, rowsOnly);
  const dedup = source?.latestDedup ?? null;
  if (!dedup) {
    return { sourceVerificationId: null, matchSummary: null, rows: [] };
  }

  const out: IdentityMatchInsightRow[] = [];
  const seen = new Set<string>();
  const sourceCcid = resolveSourceCcid(source);
  const duplicateConfirmed = hasConfirmedDuplicateHit(dedup);
  const primarySignals = hitSignalsFromDedup(dedup, source);

  const hit = dedup.matched_hit_customer;
  const candidate = asRecord(dedup.candidate_match);
  const primaryCcidRaw =
    str(hit?.ccid) ||
    str(candidate?.ccid) ||
    null;
  const primaryCcid =
    primaryCcidRaw && isSameCcid(primaryCcidRaw, sourceCcid) ? null : primaryCcidRaw;
  const closest = duplicateConfirmed
    ? (Array.isArray(dedup.closest_customers) ? dedup.closest_customers : []).filter(
        (raw) => {
          const c = asRecord(raw);
          const ccid = c ? str(c.ccid) : null;
          return ccid && !isSameCcid(ccid, sourceCcid);
        },
      )
    : [];
  const closestByCcid = new Map<string, Record<string, unknown>>();
  for (const raw of closest) {
    const c = asRecord(raw);
    const ccid = c ? str(c.ccid) : null;
    if (c && ccid) closestByCcid.set(ccid, c);
  }

  if (duplicateConfirmed && primaryCcid) {
    seen.add(primaryCcid);
    const closestTwin = closestByCcid.get(primaryCcid);
    out.push({
      id: `hit:${primaryCcid}`,
      role: 'primary_hit',
      rank: 1,
      name: hit
        ? displayName(hit)
        : displayName({
            first_name: str(candidate?.first_name) || str(closestTwin?.first_name),
            last_name: str(candidate?.last_name) || str(closestTwin?.last_name),
            name: str(candidate?.name) || str(closestTwin?.name),
          }),
      ccid: primaryCcid,
      email:
        str(hit?.email) ||
        str(candidate?.maskedEmail) ||
        str(candidate?.email) ||
        str(closestTwin?.email) ||
        str(closestTwin?.maskedEmail),
      phone:
        str(hit?.phone_number) ||
        str(candidate?.maskedPhone) ||
        str(candidate?.phone_number) ||
        str(closestTwin?.phone_number) ||
        str(closestTwin?.maskedPhone),
      distance: num(candidate?.distance) ?? num(closestTwin?.distance),
      accountStatus: str(hit?.status) || str(closestTwin?.status),
      matchSignals: primarySignals,
      selfie: parseImageRef(hit?.selfie) || parseImageRef(closestTwin?.selfie),
      document: parseImageRef(hit?.document) || parseImageRef(closestTwin?.document),
    });
  }

  const neighbors = duplicateConfirmed
    ? closest
    .map((raw, index) => {
      const c = asRecord(raw);
      if (!c) return null;
      const ccid = str(c.ccid);
      if (!ccid || seen.has(ccid)) return null;
      return {
        ccid,
        distance: num(c.distance),
        email: str(c.email) || str(c.maskedEmail),
        phone: str(c.phone_number) || str(c.maskedPhone),
        name: displayName({
          first_name: str(c.first_name),
          last_name: str(c.last_name),
          name: str(c.name),
        }),
        accountStatus: str(c.status),
        selfie: parseImageRef(c.selfie),
        document: parseImageRef(c.document),
        index,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x != null)
    .sort((a, b) => {
      if (a.distance == null && b.distance == null) return a.index - b.index;
      if (a.distance == null) return 1;
      if (b.distance == null) return -1;
      return a.distance - b.distance;
    })
    : [];

  for (const n of neighbors.slice(0, 12)) {
    seen.add(n.ccid);
    out.push({
      id: `near:${n.ccid}`,
      role: 'near_match',
      rank: out.length + 1,
      name: n.name,
      ccid: n.ccid,
      email: n.email,
      phone: n.phone,
      distance: n.distance,
      accountStatus: n.accountStatus,
      matchSignals: [{ id: 'near', label: 'Near candidate', tooltip: null }],
      selfie: n.selfie,
      document: n.document,
    });
  }

  return {
    sourceVerificationId: source?.iv_verification_id ?? null,
    matchSummary: str(dedup.message),
    rows: out,
  };
}

/** Operator copy when neighborhood has no primary hit or near candidates. */
export const IDENTITY_MATCH_NEIGHBORHOOD_EMPTY =
  "We didn't find any similar customers in the database.";


/** Lower distance = closer match. Map to 0–100 “how close” for a meter. */
export function distanceClosenessPct(distance: number | null, maxDistance = 1): number {
  if (distance == null || !Number.isFinite(distance)) return 0;
  const capped = Math.max(0, Math.min(maxDistance, distance));
  return Math.round((1 - capped / maxDistance) * 100);
}
